import { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';

import { FormProvider, type Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CTNSteps from '@ctn/ui/components/ctn-steps';
import type { CTNStepsOnClickProps, CTNStepsStepProps } from '@ctn/ui/types/ctn-steps';
import type { ProductCatalog } from '@/interfaces/strapi';
import { type CatalogRequestFormValues, buildDefaultValues, formSchema, stepFields } from './config';
import { PersonalDataStep, OrderDataStep, ResumeDataStep } from './Steps';
import type { ObjectSchema } from 'yup';
import CTNButton from '@ctn/ui/components/ctn-button';

type CatalogRequestStepperProps = {
	readonly className?: string;
	readonly defaultProduct: ProductCatalog;
	readonly onSubmitSuccess?: (payload: CatalogRequestFormValues) => void;
};

const classNameComponent = 'c-cnm-catalog-stepper';

const stepsConfig = [
	{ id: 1, title: 'Datos personales' },
	{ id: 2, title: 'Pedido' },
	{ id: 3, title: 'Confirmación' },
];

export const CatalogRequestStepper = ({ className, defaultProduct, onSubmitSuccess }: CatalogRequestStepperProps) => {
	const wrapperClassName = classNames(classNameComponent, className);

	const [activeIndex, setActiveIndex] = useState(0);
	const [maxIndex, setMaxIndex] = useState(0);

	const resolver = useMemo<Resolver<CatalogRequestFormValues>>(
		() => yupResolver<CatalogRequestFormValues>(formSchema as unknown as ObjectSchema<CatalogRequestFormValues>),
		[],
	);

	const methods = useForm<CatalogRequestFormValues>({
		mode: 'onTouched',
		resolver,
		defaultValues: buildDefaultValues(defaultProduct ?? undefined),
	});

	const { trigger, handleSubmit } = methods;

	const goTo = useCallback(
		async (nextIndex: number) => {
			// No permitir saltar a pasos no alcanzados
			if (nextIndex > maxIndex) return;

			// Validar el paso actual antes de continuar
			if (nextIndex > activeIndex) {
				const fields = stepFields[activeIndex];
				const ok = await trigger(fields, { shouldFocus: true });
				if (!ok) return;
			}

			setActiveIndex(nextIndex);
		},
		[activeIndex, maxIndex, trigger],
	);

	const onClickStep = useCallback(({ index }: CTNStepsOnClickProps) => goTo(index), [goTo]);

	const next = useCallback(async () => {
		const fields = stepFields[activeIndex];
		const ok = await trigger(fields, { shouldFocus: true });
		if (!ok) return;

		const nextIdx = Math.min(activeIndex + 1, stepsConfig.length - 1);
		setActiveIndex(nextIdx);
		setMaxIndex((prev) => Math.max(prev, nextIdx));
	}, [activeIndex, trigger]);

	const back = useCallback(() => {
		const prevIdx = Math.max(0, activeIndex - 1);
		setActiveIndex(prevIdx);
	}, [activeIndex]);

	const submitAll = handleSubmit((data: CatalogRequestFormValues) => {
		// Si tu backend quiere un string combinado:
		const payload: CatalogRequestFormValues & { movil: any } = {
			...data,
		};

		onSubmitSuccess?.(payload as CatalogRequestFormValues);
	});

	const steps: CTNStepsStepProps[] = useMemo(
		() =>
			stepsConfig.map((s, i) => ({
				id: s.id,
				title: s.title,
				active: i <= maxIndex || i === activeIndex,
			})),
		[activeIndex, maxIndex],
	);

	return (
		<div className={wrapperClassName}>
			<CTNSteps activeIndex={activeIndex} maxIndex={maxIndex} steps={steps} onClick={onClickStep} />

			<FormProvider {...methods}>
				<form noValidate className={`${classNameComponent}__form`} onSubmit={(e) => e.preventDefault()}>
					{activeIndex === 0 && <PersonalDataStep title={stepsConfig[0].title} />}
					{activeIndex === 1 && <OrderDataStep title={stepsConfig[1].title} />}
					{activeIndex === 2 && <ResumeDataStep title={stepsConfig[2].title} />}

					<div className={`${classNameComponent}__actions o-grid__row o-grid__row--justify-between`}>
						<div>
							{activeIndex > 0 && (
								<CTNButton
									type="button"
									variant="solid"
									mode="primary"
									className={`${classNameComponent}__btn`}
									label="Atras"
									onClick={back}
								/>
							)}
						</div>

						<div>
							{activeIndex < stepsConfig.length - 1 ? (
								<CTNButton
									type="button"
									variant="solid"
									mode="primary"
									className={`${classNameComponent}__btn`}
									label="Siguiente"
									onClick={next}
								/>
							) : (
								<CTNButton
									type="button"
									variant="solid"
									mode="primary"
									className={`${classNameComponent}__btn`}
									label="Confirmar pedido"
									onClick={submitAll}
								/>
							)}
						</div>
					</div>
				</form>
			</FormProvider>
		</div>
	);
};
